import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AsyncPipe, NgFor } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { DateAdapter, MatOption } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle, MatDatepickerToggleIcon } from '@angular/material/datepicker';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { OAHelper } from '@app/api/core/utils/helper/offerteantraege-helper';
import { DateStringAdapter } from '@app/shared/components/form/basic-datepicker/datestring.adapter';
import { UserService } from '@app/shared/services/data/user.service';
import { ExpandableRow, ExpandedRowAddition, ListData, ListTyp, OffertStageTwoRowAddition } from '@app/views/vertriebscockpit/offerteantraege/offerteantraege.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PolizzeResult, VkeTyp } from '@o30/entities';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormatDatePipe } from '../../pipes/formatdate.pipe';

type RowType =
  ExpandableRow<PolizzeResult>
  | ExpandedRowAddition<PolizzeResult>
  | OffertStageTwoRowAddition<PolizzeResult>;

@UntilDestroy()
@Component({
  selector:    'app-filter-search',
  templateUrl: './filter-search.component.html',
  styleUrls:   ['./filter-search.component.scss'],
  standalone:  true,
  providers:   [
    {
      provide:  DateAdapter,
      useClass: DateStringAdapter
    }
  ],
  imports:     [
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatFormField,
    MatChipGrid,
    NgFor,
    MatChipRow,
    MatChipRemove,
    FormsModule,
    MatChipInput,
    ReactiveFormsModule,
    MatMenu,
    MatMenuItem,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepickerToggleIcon,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatButton,
    AsyncPipe
  ]
})
export class FilterSearchComponent implements OnInit,
                                              OnDestroy {

  private COL_BEGINNDATUM = 'beginndatum';
  private COL_POL_NR = 'polizzennummer';
  private COL_BEARBEITET = 'bearbeitet';
  private COL_KFZKZ = 'kfzkennzeichen';
  private COL_VKETYP = 'vketyp';
  private COL_BETREUER = 'betreuernummer';
  private COL_ERSTELLDATUM = 'erstelldatum';

  protected userService = inject(UserService);

  public vkeTypen = Object.keys(VkeTyp)
    .map(key => VkeTyp[key])
    .filter(value => typeof value === 'string')
    .map(word => word[0].toUpperCase() + word.substr(1)
      .toLowerCase());

  @Input()
  allDataSources: Map<ListTyp, ListData>;

  @Output()
  public afterFilter = new EventEmitter();

  @ViewChild('filterInput', {static: true}) filterInput: ElementRef<HTMLInputElement>;
  @ViewChild('menuTrigger', {static: true}) filterMenuTrigger: MatMenuTrigger;

  public polNr: string;
  public beginFromDate: string;
  public beginToDate: string;
  public btrNr: string;
  public bearbeiter: string;
  public kfzKz: string;
  public vkeTyp: string;
  public erstellFromDate: string;
  public erstellToDate: string;

  public inputCtrl = new UntypedFormControl();

  // KeyboardEvent.keycode & KeyboardEvent.which are deprecated
  // this might not work at some point
  public separatorKeysCodes = [
    ENTER,
    COMMA
  ];
  public currentChips: FilterChip[] = [];

  ngOnInit() {
    this.initBasicFilter();
    this.inputCtrl.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe(searchTerm => {
        this.useFilter();
      });
  }

  ngOnDestroy(): void {
    // untilDestroyed
  }

  public fillMenu(openMenu: boolean): void {
    this.clearMenu();
    this.currentChips.forEach(chip => {
      if (chip.filter_mode === Filter_Mode.DATE_TO) {
        if (chip.filter_column === this.COL_BEGINNDATUM) {
          this.beginToDate = chip.filtertext;
        }
        if (chip.filter_column === this.COL_ERSTELLDATUM) {
          this.erstellToDate = chip.filtertext;
        }
      }
      if (chip.filter_mode === Filter_Mode.DATE_FROM) {
        if (chip.filter_column === this.COL_BEGINNDATUM) {
          this.beginFromDate = chip.filtertext;
        }
        if (chip.filter_column === this.COL_ERSTELLDATUM) {
          this.erstellFromDate = chip.filtertext;
        }
      }

      if (chip.filter_mode === Filter_Mode.SINGLE_COL) {
        if (chip.filter_column === this.COL_POL_NR) {
          this.polNr = chip.filtertext;
        }
        if (chip.filter_column === this.COL_BEARBEITET) {
          this.bearbeiter = chip.filtertext;
        }
        if (chip.filter_column === this.COL_BETREUER) {
          this.btrNr = chip.filtertext;
        }
        if (chip.filter_column === this.COL_VKETYP) {
          this.vkeTyp = chip.filtertext;
        }
      }
    });
    if (openMenu) {
      this.filterMenuTrigger.openMenu();
    }
  }

  private useFilter(): void {
    this.allDataSources.forEach(ds => ds.dataSource.filter = JSON.stringify(this.currentChips));
    this.afterFilter.emit(this.inputCtrl.value);
  }

  public removeChip(category: FilterChip): void {
    this.filterInput.nativeElement.focus();
    const index = this.currentChips.findIndex(c => c.filtertext === category.filtertext && c.label === category.label);

    if (index >= 0) {
      this.currentChips.splice(index, 1);
    }
    this.useFilter();
  }

  private initBasicFilter(): void {
    this.allDataSources.forEach(ds => ds.dataSource.filterPredicate = (data: RowType, filter: string) => {
      const searchTerms: FilterChip[] = JSON.parse(filter);

      if (this.inputCtrl.value) {
        searchTerms.push({
          filtertext:  this.inputCtrl.value.toLowerCase(),
          filter_mode: Filter_Mode.DEFAULT,
          label:       'wurscht'
        });
      }
      // entweder gibt es gar keinen suchparameter → true / oder alle suchparameter liefern true für diese Zeile
      return !searchTerms || searchTerms.every(f => this.mapFilterFunction(data, f));
    });
  }

  private mapFilterFunction(row: RowType, filter: FilterChip): boolean {
    switch (filter.filter_mode) {
      case Filter_Mode.DEFAULT:
        return this.filterStr(row, filter.filtertext);
      case Filter_Mode.SINGLE_COL:
        return this.filterStrSingleCol(row, filter.filtertext, filter.filter_column);
      case Filter_Mode.DATE_FROM:
        return this.filterDate(row, filter, true);
      case Filter_Mode.DATE_TO:
        return this.filterDate(row, filter, false);
    }
    return false;
  }

  private filterStr(row: RowType, filter: string): boolean {
    if (row.data) {
      return OAHelper.filterColumns.some(col => this.getFilterResult(row.data, filter, col));
    }
    return false;
  }

  private filterStrSingleCol(row: RowType, filter: string, column: string): boolean {
    if (row.data) {
      return this.getFilterResult(row.data, filter, column);
    }
    return false;
  }

  private filterDate(row: RowType, filter: FilterChip, from: boolean) {
    if (row.data[filter.filter_column]) {
      const rowDate = new Date(Date.parse(row.data[filter.filter_column].toString()));
      const filterDate = new Date(Date.parse(filter.filtertext));
      if (from) {
        return rowDate >= filterDate;
      } else {
        return rowDate <= filterDate;
      }
    }
    return false;
  }

  private getFilterResult(value: PolizzeResult, filter: string, col: string): boolean {
    if (value) {
      if (col === this.COL_VKETYP) {
        return value.vke.typ
          .toLowerCase() === filter.toLowerCase();
      } else if (col === this.COL_BEARBEITET) {
        return value.bearbeitet.userId
          .toLowerCase()
          .indexOf(filter) !== -1;
      } else {
        if (value[col] !== null && value[col] !== undefined) {
          return value[col].toString()
            .toLowerCase()
            .indexOf(filter) !== -1;
        }
      }
    }
    return false;
  }

  public applyFilter(): void {
    this.filterMenuTrigger.closeMenu();
    if (this.polNr) {
      const trimmedPolNr = this.polNr.replace(/\D/g, '');
      if (trimmedPolNr) {
        this.removeChipByColumn(this.COL_POL_NR);
        this.currentChips.push({
            label:         'Polizzen-Nr: ' + trimmedPolNr,
            filter_column: this.COL_POL_NR,
            filter_mode:   Filter_Mode.SINGLE_COL,
            filtertext:    trimmedPolNr
          }
        );
      }
    }
    if (this.beginFromDate) {
      this.removeChipByColumnMode(this.COL_BEGINNDATUM, Filter_Mode.DATE_FROM);
      this.currentChips.push({
          label:         'Ab Beginndatum: ' + new FormatDatePipe().transform(this.beginFromDate),
          filter_column: this.COL_BEGINNDATUM,
          filter_mode:   Filter_Mode.DATE_FROM,
          filtertext:    this.beginFromDate
        }
      );
    }
    if (this.beginToDate) {
      this.removeChipByColumnMode(this.COL_BEGINNDATUM, Filter_Mode.DATE_TO);
      this.currentChips.push({
          label:         'Bis Beginndatum: ' + new FormatDatePipe().transform(this.beginToDate),
          filter_column: this.COL_BEGINNDATUM,
          filter_mode:   Filter_Mode.DATE_TO,
          filtertext:    this.beginToDate
        }
      );
    }
    if (this.bearbeiter) {
      this.removeChipByColumnMode(this.COL_BEARBEITET, Filter_Mode.SINGLE_COL);
      this.currentChips.push({
          label:         'Bearbeiter:' + this.bearbeiter,
          filter_column: this.COL_BEARBEITET,
          filter_mode:   Filter_Mode.SINGLE_COL,
          filtertext:    this.bearbeiter.toLowerCase()
        }
      );
    }
    if (this.kfzKz) {
      this.removeChipByColumnMode(this.COL_KFZKZ, Filter_Mode.SINGLE_COL);
      this.currentChips.push({
          label:         'Kennzeichen:' + this.kfzKz,
          filter_column: this.COL_KFZKZ,
          filter_mode:   Filter_Mode.SINGLE_COL,
          filtertext:    this.kfzKz.toLowerCase()
        }
      );
    }
    if (this.btrNr) {
      this.removeChipByColumnMode(this.COL_BETREUER, Filter_Mode.SINGLE_COL);
      this.currentChips.push({
          label:         'Betreuer: ' + this.btrNr,
          filter_column: this.COL_BETREUER,
          filter_mode:   Filter_Mode.SINGLE_COL,
          filtertext:    this.btrNr
        }
      );
    }
    if (this.erstellFromDate) {
      this.removeChipByColumnMode(this.COL_ERSTELLDATUM, Filter_Mode.DATE_FROM);
      this.currentChips.push({
          label:         'Ab Erstelldatum: ' + new FormatDatePipe().transform(this.erstellFromDate),
          filter_column: this.COL_ERSTELLDATUM,
          filter_mode:   Filter_Mode.DATE_FROM,
          filtertext:    this.erstellFromDate
        }
      );
    }
    if (this.erstellToDate) {
      this.removeChipByColumnMode(this.COL_ERSTELLDATUM, Filter_Mode.DATE_TO);
      this.currentChips.push({
          label:         'Bis Erstelldatum: ' + new FormatDatePipe().transform(this.erstellToDate),
          filter_column: this.COL_ERSTELLDATUM,
          filter_mode:   Filter_Mode.DATE_TO,
          filtertext:    this.erstellToDate
        }
      );
    }
    if (this.vkeTyp) {
      this.removeChipByColumnMode(this.COL_VKETYP, Filter_Mode.SINGLE_COL);
      this.currentChips.push({
          label:         'Vke Typ:' + this.vkeTyp,
          filter_column: this.COL_VKETYP,
          filter_mode:   Filter_Mode.SINGLE_COL,
          filtertext:    this.vkeTyp
        }
      );
    }
    this.clearMenu();
    this.useFilter();
  }

  private clearMenu(): void {
    this.polNr = null;
    this.beginFromDate = null;
    this.beginToDate = null;
    this.bearbeiter = null;
    this.btrNr = null;
    this.kfzKz = null;
    this.erstellFromDate = null;
    this.erstellToDate = null;
    this.vkeTyp = null;
  }

  private removeChipByColumn(col: string): void {
    const idx = this.currentChips.findIndex(chip => chip.filter_column === col);
    if (idx !== -1) {
      this.currentChips.splice(idx, 1);
    }
  }

  private removeChipByColumnMode(col: string, mode: Filter_Mode): void {
    const idx = this.currentChips.findIndex(chip => chip.filter_column === col && chip.filter_mode === mode);
    if (idx !== -1) {
      this.currentChips.splice(idx, 1);
    }
  }
}

export interface FilterChip {
  label: string;
  filter_mode?: Filter_Mode;
  filter_column?: string;
  filtertext: string;
}

export enum Filter_Mode {
  DATE_FROM = 'date_from',
  DATE_TO = 'date_to',
  SINGLE_COL = 'single_col',
  DEFAULT = 'default'
}
